import { useState } from "react";
import { Trash2, Upload, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/labels";

const Lists = ({ lists, canManage }) => {
  const [name, setName] = useState("");
  const add = async () => { try { await api.post("/company/contact-lists", { name }); setName(""); lists.reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="card-surface p-5" data-testid="contact-lists">
      <p className="font-display font-semibold">Listes</p>
      <ul className="mt-2 space-y-1 text-sm">{(lists.data || []).map((l) => (
        <li key={l.id} className="flex items-center justify-between" data-testid={`list-${l.id}`}><span>{l.name} · {l.count} inscrit(s)</span>
          {canManage && <button onClick={async () => { await api.delete(`/company/contact-lists/${l.id}`); lists.reload(); }} className="text-rose-600" data-testid={`list-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</li>))}</ul>
      {canManage && <div className="mt-3 flex gap-2"><Input placeholder="Nouvelle liste" value={name} onChange={(e) => setName(e.target.value)} data-testid="list-name-input" /><Button onClick={add} data-testid="list-add-button"><Plus className="h-4 w-4" /></Button></div>}
    </div>
  );
};

const Import = ({ lists, onDone }) => {
  const [f, setF] = useState({ csv: "", list_id: "", consent_confirmed: false });
  const submit = async () => {
    try { const { data } = await api.post("/company/contacts/import", { ...f, list_id: f.list_id || null }); toast.success(`${data.imported} contact(s) importé(s)${data.invalid_lines.length ? `, lignes invalides : ${data.invalid_lines.join(", ")}` : ""}`); setF({ ...f, csv: "" }); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="card-surface space-y-3 p-5" data-testid="contacts-import">
      <p className="flex items-center gap-2 font-display font-semibold"><Upload className="h-4 w-4" />Importer (CSV)</p>
      <Textarea rows={4} placeholder={"email;nom;pays\nmarie@exemple.com;Marie;Sénégal"} value={f.csv} onChange={(e) => setF({ ...f, csv: e.target.value })} data-testid="import-csv-input" />
      <select className="field-select" value={f.list_id} onChange={(e) => setF({ ...f, list_id: e.target.value })} data-testid="import-list-select"><option value="">Sans liste</option>{(lists || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.consent_confirmed} onCheckedChange={(v) => setF({ ...f, consent_confirmed: !!v })} data-testid="import-consent-checkbox" />Je certifie que chaque contact a donné son consentement explicite à recevoir nos communications.</label>
      <Button onClick={submit} disabled={!f.consent_confirmed || !f.csv.trim()} data-testid="import-submit-button">Importer</Button>
    </div>
  );
};

export default function ContactsTab({ canManage }) {
  const [q, setQ] = useState("");
  const contacts = useApi(`/company/contacts?q=${encodeURIComponent(q)}`);
  const lists = useApi("/company/contact-lists");
  const d = contacts.data;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-3 lg:col-span-2">
        <p className="text-sm text-slate-600" data-testid="contacts-summary">{d ? `${d.subscribed} inscrit(s) · ${d.unsubscribed} désinscrit(s)` : "…"} — sources : usagers ayant accepté vos communications, imports avec consentement.</p>
        <Input placeholder="Rechercher un contact" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search" />
        <div className="card-surface divide-y" data-testid="contacts-list">
          {d?.items.map((c) => (
            <div key={c.id} className="flex items-center gap-2 px-4 py-2.5 text-sm" data-testid={`contact-${c.id}`}>
              <span className="flex-1">{c.name || "—"} · {c.email} {c.country && <span className="text-xs text-slate-400">· {c.country}</span>}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${c.status === "subscribed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.status === "subscribed" ? "Inscrit" : "Désinscrit"}</span>
              <span className="text-xs text-slate-400">{c.consent_source === "feedback" ? "via avis" : "import"} · {fmtDate(c.consent_at, false)}</span>
              {canManage && <button onClick={async () => { await api.delete(`/company/contacts/${c.id}`); contacts.reload(); }} className="text-rose-600" data-testid={`contact-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          {d && !d.items.length && <p className="p-4 text-sm text-slate-500">Aucun contact.</p>}
        </div>
      </div>
      <div className="space-y-6"><Lists lists={lists} canManage={canManage} />{canManage && <Import lists={lists.data} onDone={() => { contacts.reload(); lists.reload(); }} />}</div>
    </div>
  );
}
